/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { useSendAIChatMutation } from '../../../api/initiative-data-integrations';
import { AIMessageMetadata, ChatMessage } from './partials/AIMessageMetadata';
import { DATE, formatDate } from '../../../utils/date';
import { PreviewAlert } from '@g17eco/molecules/feature-stability/PreviewAlert';
import { loggerMessage } from '../../../logger';

const suggestedQuestions = [
  'What\'s our Scope 1, 2 and 3 carbon emissions trends?',
  'Show me our water usage trends',
  'Show me our energy usage per year',
  'What are our indirect economic impacts?',
  'What is our employment by type?',
];

interface QuestionViewContext {
  type: 'QuestionView';
  utrId: string;
  utrvId: string;
}

interface AIChatProps {
  initiativeId: string;
  conversationId?: string;
  initialContext?: QuestionViewContext;
}

export const AIChat: React.FC<AIChatProps> = ({
  initiativeId,
  conversationId,
  initialContext,
}) => {
  const [inputMessage, setInputMessage] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Send chat query
  const [sendChat, { isLoading: isSending }] = useSendAIChatMutation();

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    setTimeout(scrollToBottom, 100);
  }, []);

  // Auto-send initial prompt if provided
  useEffect(() => {
    if (!initialContext) {
      return;
    }
    const handleSendInitialContext = async (context: QuestionViewContext) => {
      // Add user message immediately
      addMessage({
        role: 'assistant',
        content: 'I am anaylzing your data to find any relevant information to help you answer your question.',
      });

      try {
        const response = await sendChat({
          initiativeId,
          context,
          conversationId,
        }).unwrap();

        // Add assistant response with new structure
        addMessage({
          role: 'assistant',
          content: response.response || 'Response received',
          metadata: {
            sql_query: response.sql_query,
            data: response.data,
            suggested_questions: response.suggested_questions,
            conversation_id: response.conversation_id,
            warnings: response.warnings,
            response_metadata: response.response_metadata,
          },
        });
      } catch (error) {
        loggerMessage('Failed to send initial query:', error);
        addMessage({
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your initial request. Please try again.',
          metadata: { error: 'Initial request failed' },
        });
      }
    };
    handleSendInitialContext(initialContext);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSuggestionClick = (question: string) => {
    setInputMessage(question);
    // Auto-send the suggestion or just populate the input
    // For better UX, let's just populate so user can review before sending
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();

    // Add user message immediately
    addMessage({
      role: 'user',
      content: userMessage,
    });

    setInputMessage('');

    try {
      const response = await sendChat({
        initiativeId,
        message: userMessage,
        conversationId: conversationId || undefined,
      }).unwrap();

      // Add assistant response with new structure
      addMessage({
        role: 'assistant',
        content: response.response || 'Response received',
        metadata: {
          sql_query: response.sql_query || undefined,
          data: response.data || undefined,
          suggested_questions: response.suggested_questions || undefined,
          conversation_id: response.conversation_id || undefined,
          warnings: response.warnings || undefined,
          response_metadata: response.response_metadata || undefined,
        },
      });
    } catch (error) {
      console.error('Failed to send query:', error);
      addMessage({
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        metadata: { error: 'Request failed' },
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessage = (msg: ChatMessage) => {
    const timestamp = formatDate(msg.timestamp, DATE.HUMANIZE);
    const isUser = msg.role === 'user';

    return (
      <div key={msg.id} className={`d-flex mb-3 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
        {/* Avatar for assistant */}
        {!isUser && (
          <div className='me-2'>
            <div className='bg-primary rounded-circle d-flex align-items-center justify-content-center' style={{ width: '32px', height: '32px', minWidth: '32px' }}>
              <i className='fas fa-robot text-white fa-sm' />
            </div>
          </div>
        )}

        {/* Message bubble */}
        <div className={`rounded px-3 py-2 shadow-sm overflow-hidden ${isUser ? 'bg-primary text-white' : 'bg-white'}`} style={{ maxWidth: '80%' }}>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <small className={`fw-bold ${isUser ? 'text-ThemeTextLight' : 'text-primary'}`}>
              {isUser ? 'You' : 'AI Databot'}
            </small>
            <small className={`ms-2 ${isUser ? 'text-ThemeTextLight' : 'text-muted'}`}>
              {timestamp}
            </small>
          </div>

          <div className='message-content mb-0'>
            {msg.content}
          </div>

          {/* Metadata for assistant messages */}
          {!isUser && msg.metadata && (
            <AIMessageMetadata
              metadata={msg.metadata}
              messageId={msg.id}
              onSuggestionClick={handleSuggestionClick}
            />
          )}
        </div>

        {/* Avatar for user */}
        {isUser && (
          <div className='ms-2'>
            <div className='bg-secondary rounded-circle d-flex align-items-center justify-content-center' style={{ width: '32px', height: '32px', minWidth: '32px' }}>
              <i className='fas fa-user text-white fa-sm' />
            </div>
          </div>
        )}
      </div>
    );
  };

  const lastMessage = messages[messages.length - 1];

  return (
    <div className='h-100 d-flex flex-column'>
      <PreviewAlert />
      {/* Messages Area */}
      {lastMessage?.metadata?.warnings && lastMessage?.metadata?.warnings.length > 0 && (
        <div className='alert alert-warning'>
          {lastMessage?.metadata?.warnings.map((warning) => warning.message).join(', ')}
        </div>
      )}
      <div className='flex-grow-1 border bg-light p-3' style={{ overflowY: 'auto' }}>
        {messages.length === 0 ? (
          <div className='text-center py-5'>
            <i className='fal fa-comments fa-4x text-muted mb-3' />
            <h5 className='text-muted mb-2'>Start a conversation</h5>
            <p className='text-muted mb-4'>Ask questions about your data and get insights instantly.</p>

            <div className='row justify-content-center'>
              <div className='col-md-8'>
                <div className='bg-white rounded p-3 mb-3'>
                  <h6 className='text-primary mb-3'>
                    <i className='fal fa-lightbulb me-2' />
                    Try asking:
                  </h6>
                  <div className='d-flex flex-column gap-2'>
                    {suggestedQuestions.map((question) => (
                      <button
                        key={question}
                        className='btn btn-outline-primary btn-sm text-start'
                        onClick={() => handleSuggestionClick(question)}
                      >
                        <i className='fal fa-comment-dots me-2' />
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {messages.map(formatMessage)}
            {isSending && (
              <div className='d-flex mb-3'>
                <div className='me-2'>
                  <div className='bg-primary rounded-circle d-flex align-items-center justify-content-center' style={{ width: '32px', height: '32px' }}>
                    <i className='fas fa-robot text-white fa-sm' />
                  </div>
                </div>
                <div className='bg-white rounded px-3 py-2 shadow-sm'>
                  <div className='d-flex align-items-center'>
                    <Spinner size='sm' />
                    <span className='ms-2 text-muted'>Analyzing your data query...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className='bg-white border-top py-3'>
        <div className='d-flex gap-2'>
          <Input
            type='textarea'
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder='Ask a question about your data...'
            rows={3}
            disabled={isSending}
            className='flex-grow-1'
            style={{ resize: 'none' }}
          />
          <Button
            color='primary'
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isSending}
            style={{ minWidth: '60px', height: 'auto' }}
          >
            {isSending ? (
              <i className='fas fa-spinner fa-spin' />
            ) : (
              <i className='fal fa-paper-plane' />
            )}
          </Button>
        </div>
        <small className='text-muted d-block mt-1'>
          <i className='fal fa-info-circle me-1' />
          Press Enter to send, Shift+Enter for new line
        </small>
      </div>
    </div>
  );
};
